<script setup lang="ts">
import { bemBuilder } from "@chatfood/core-utils";
import { ref } from "vue";
import { useToast, SatButton } from "@chatfood/design-system";
import { useBasketStore } from "~/stores/basket";
import { useFeatureMatrixStore } from "~/stores/featureMatrix";
import { useServiceError } from "~/composables/use-service-error";
import { useLocale } from "~/composables/use-locale";
import { useNavigation } from "~/composables/use-navigation";
import { isBasketRepoError } from "@chatfood/dbe-client/errors";
import type { ReorderButtonProps } from "./reorder-button.props";
import { useBugReporter, useDirectBackend } from "#imports";

const css = bemBuilder("reorder-button");

const props = defineProps<ReorderButtonProps>();

const { t } = useLocale();
const { push } = useNavigation();
const { toastSuccess, toastError, toastUnexpectedError } = useToast();
const { handleBusinessRuleError } = useServiceError();
const { outletRepo } = useDirectBackend();
const { report } = useBugReporter();

const basket = useBasketStore();
const featureMatrix = useFeatureMatrixStore();

const isLoading = ref(false);

async function handleReorder() {
  isLoading.value = true;

  try {
    await basket.recreateBasket(props.orderId, props.channelLinkId);

    await handleReorderSuccess();
  } catch (error) {
    if (isBasketRepoError(error)) {
      if (error.isBusinessRule()) {
        handleBusinessRuleError(error);
        return;
      }

      if (error.isNotFound()) {
        toastError(t("components.reorder_button.error_order_not_found"));
        return;
      }
    }

    report(error);

    const errorMesssage =
      error instanceof Error
        ? error.message
        : typeof error === "string"
          ? error
          : undefined;

    if (errorMesssage) {
      toastError(errorMesssage);
    } else {
      toastUnexpectedError();
    }
  } finally {
    isLoading.value = false;
  }
}

async function handleReorderSuccess() {
  try {
    const res = await outletRepo.getOutletById({
      outletId: basket.outletId,
      salesChannel: basket.salesChannel,
      orderingMode: basket.orderingMode,
    });

    await push("outlet", {
      orderingMode: basket.orderingMode,
      // TODO: we could use 'basket.outletId' if all businesses were using the Commerce API
      outletSlug: res.slug,
    });

    toastSuccess(t("components.reorder_button.success_message"));
  } catch (error) {
    report(error);
    toastUnexpectedError();
  }
}
</script>

<template>
  <SatButton
    v-if="featureMatrix.isToggleEnabled('enable-reorder')"
    :class="css()"
    size="small"
    theme="ghost"
    variant="primary"
    :loading="isLoading"
    @click.prevent="handleReorder"
  >
    {{ t("components.reorder_button.cta_label") }}
  </SatButton>
</template>
